import React, { useState } from "react";
import { useRouter } from "next/router";

import useUser from "@src/features/app/hooks/useUser";
import useLanguage from "@src/features/contentDetail/hooks/useLanguage";
import { UserType } from "@src/types.generated";
import { useTranslation } from "@src/features/contentDetail/utils/translationSets";
import { getFirstNameAndLastInitial } from "@src/features/app/top-nav/utils";
import { useAdminToolsMenu } from "@src/features/app/top-nav/AdminTools";
import { Button } from "@src/lib/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import ArrowLogoutIcon from "@src/lib/icons/18px/arrow-logout";
import CircleUserOutlineIcon from "@src/lib/icons/18px/circle-user-outline";
import StudentIcon from "@src/lib/icons/18px/student";
import ChevronsOpenIcon from "@src/lib/icons/12px/chevrons-open";

type Props = {
  variant?: "admin" | "trainee";
};

const TopNavProfileMenu: React.FC<Props> = ({ variant = "admin" }) => {
  const router = useRouter();
  const { user } = useUser();
  const language = useLanguage();
  const [open, setOpen] = useState(false);

  const profileTranslation = useTranslation("Profile", language || "en");
  const myTrainingTranslation = useTranslation("My Training", language || "en");
  const logoutTranslation = useTranslation("Logout", language || "en");
  const { menuItem: adminToolsMenuItem, dialog: adminToolsDialog } =
    useAdminToolsMenu();

  if (!user) return null;

  const displayName = getFirstNameAndLastInitial(user.name);
  const isTraineeNav = variant === "trainee";

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="max-w-[160px] gap-1.5 px-2 data-[state=open]:bg-black-alpha-2"
          >
            <CircleUserOutlineIcon className="size-4 shrink-0" />
            <span className="truncate">{displayName}</span>
            <ChevronsOpenIcon className="!size-3 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-56 rounded-lg"
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-medium">{user.name}</div>
            <div className="text-xs text-gray-9">{user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {isTraineeNav ? (
              <DropdownMenuItem
                onClick={() => {
                  router.push("/my-profile");
                  setOpen(false);
                }}
              >
                <CircleUserOutlineIcon />
                {profileTranslation.text || "Profile"}
              </DropdownMenuItem>
            ) : (
              user.userType !== UserType.Employee && (
                <DropdownMenuItem
                  onClick={() => {
                    router.push({
                      pathname: "/users/[id]",
                      query: { id: user.id.toString() },
                    });
                    setOpen(false);
                  }}
                >
                  <CircleUserOutlineIcon />
                  {profileTranslation.text || "Profile"}
                </DropdownMenuItem>
              )
            )}
            <DropdownMenuItem
              onClick={() => {
                router.push("/my-training");
                setOpen(false);
              }}
            >
              <StudentIcon />
              {myTrainingTranslation.text || "My Training"}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          {adminToolsMenuItem && (
            <>
              <DropdownMenuGroup>{adminToolsMenuItem}</DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            onClick={() => {
              router.push("/logout");
              setOpen(false);
            }}
          >
            <ArrowLogoutIcon />
            {logoutTranslation.text || "Logout"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {adminToolsDialog}
    </>
  );
};

export default TopNavProfileMenu;
